// Auth library - user authentication, session management, and admin operations
// CHANGE: set_password_reset_metadata RPC now takes only p_user_id; password_reset_by is derived from auth.uid() server-side
// CHANGE: Login failure logging routed through log-login-error Edge Function (rate-limited, validated server-side)
// CHANGE: Added apikey header to all Edge Function fetch calls for gateway compatibility

import { supabase } from './supabase';
import { envConfig } from './envConfig';
import type { User, UserWithSites, Site, UserRole } from './supabase';

function logLoginError(username: string, error: unknown, stage: string): void {
  try {
    const errMsg = error instanceof Error ? error.message : String(error);
    const errName = error instanceof Error ? error.name : typeof error;
    fetch(`${envConfig.supabaseUrl}/functions/v1/log-login-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Apikey': envConfig.supabaseAnonKey,
      },
      body: JSON.stringify({
        username,
        error_message: errMsg,
        error_type: errName,
        stage,
        user_agent: navigator.userAgent || 'unknown',
        error_stack: error instanceof Error ? (error.stack || '').slice(0, 1000) : null,
        url: window.location.href,
      }),
    }).catch(() => {});
  } catch {
    // Best-effort: never let logging break the login flow
  }
}

export interface AuthResponse {
  success: boolean;
  error?: string;
  user?: UserWithSites;
  mustChangePassword?: boolean;
}

export function isNetworkError(error: any): boolean {
  // Check for TypeError with fetch (common in network failures)
  if (error instanceof TypeError && error.message?.includes('fetch')) {
    return true;
  }

  // Check error message for network-related keywords
  const errorMessage = error?.message || String(error);
  return (
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('network') ||
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('offline') ||
    error?.status === 0
  );
}

// Helper function to load user sites via Edge Function
async function loadUserSites(userId: string): Promise<Site[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return [];
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/get-user-sites`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': envConfig.supabaseAnonKey,
      },
      body: JSON.stringify({ userId }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return [];
    }

    return result.sites || [];
  } catch (error) {
    return [];
  }
}

// Sign in with username and password
export async function signIn(username: string, password: string): Promise<AuthResponse> {
  try {
    // First, get user by username to find their auth email and check if active
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id, auth_email, is_active')
      .eq('username', username)
      .maybeSingle();

    if (userError) {
      if (isNetworkError(userError)) {
        return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
      }
      return { success: false, error: 'Identifiant ou mot de passe incorrect' };
    }

    if (!userData) {
      return { success: false, error: 'Identifiant ou mot de passe incorrect' };
    }

    // Check if user is active
    if (!userData.is_active) {
      return { success: false, error: 'Votre compte a été désactivé. Contactez votre administrateur.' };
    }

    // Sign in with Supabase auth using the stored auth email
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: userData.auth_email,
      password: password,
    });

    if (authError) {
      if (isNetworkError(authError)) {
        return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
      }
      return { success: false, error: 'Identifiant ou mot de passe incorrect' };
    }

    if (!authData.user) {
      return { success: false, error: 'Identifiant ou mot de passe incorrect' };
    }

    // Get full user profile
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', authData.user.id)
      .single();

    if (profileError) {
      if (isNetworkError(profileError)) {
        return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
      }
      return { success: false, error: 'Erreur lors de la récupération du profil' };
    }

    if (!profile) {
      return { success: false, error: 'Erreur lors de la récupération du profil' };
    }

    // Double-check if user is still active (extra security layer)
    if (!profile.is_active) {
      await supabase.auth.signOut();
      return { success: false, error: 'Votre compte a été désactivé. Contactez votre administrateur.' };
    }

    // Load user's assigned sites via Edge Function (avoids RLS recursion)
    const sites = await loadUserSites(profile.id);

    // Update last activity
    await updateLastActivity(authData.user.id);

    // Check if user must change password
    if (profile.must_change_password) {
      return { success: true, user: { ...profile, sites }, mustChangePassword: true };
    }

    return { success: true, user: { ...profile, sites } };
  } catch (error) {
    logLoginError(username, error, 'signIn_catch');

    if (isNetworkError(error)) {
      return { success: false, error: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' };
    }

    return { success: false, error: 'Une erreur est survenue lors de la connexion' };
  }
}

// Sign out
export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

export async function loadUserProfile(authUserId: string): Promise<UserWithSites | null> {
  try {
    const { data: profile } = await supabase
      .from('users')
      .select('*')
      .eq('id', authUserId)
      .single();

    if (!profile) return null;

    if (!profile.is_active) {
      await supabase.auth.signOut();
      return null;
    }

    const sites = await loadUserSites(profile.id);
    return { ...profile, sites };
  } catch (error) {
    return null;
  }
}

export async function getCurrentUser(): Promise<UserWithSites | null> {
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return null;
    return loadUserProfile(authUser.id);
  } catch (error) {
    return null;
  }
}

// Update last activity timestamp
export async function updateLastActivity(userId: string): Promise<void> {
  await supabase
    .from('users')
    .update({ last_activity: new Date().toISOString() })
    .eq('id', userId);
}

// Change password
export async function changePassword(newPassword: string, clearMustChangeFlag = true): Promise<AuthResponse> {
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();

    if (!authUser) {
      return { success: false, error: 'Non authentifié' };
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      return { success: false, error: 'Erreur lors du changement de mot de passe' };
    }

    // Clear the must_change_password flag if requested
    if (clearMustChangeFlag) {
      const { error: updateError } = await supabase
        .from('users')
        .update({ must_change_password: false })
        .eq('id', authUser.id);

      if (updateError) {
        return { success: false, error: 'Erreur lors de la mise à jour du profil' };
      }
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Create a new user (Admin only) - Uses Edge Function to avoid session interference
// Auto-generates temporary password and returns it to admin
export async function createUser(
  username: string,
  firstName: string,
  lastName: string,
  password: string, // Ignored - will be auto-generated
  role: UserRole,
  siteIds?: string[]
): Promise<AuthResponse & { tempPassword?: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    // Generate temporary password using SQL function
    const { data: tempPasswordData, error: generateError } = await supabase
      .rpc('generate_temp_password');

    if (generateError) {
      return { success: false, error: 'Erreur lors de la génération du mot de passe temporaire' };
    }

    if (!tempPasswordData) {
      return { success: false, error: 'Erreur lors de la génération du mot de passe temporaire' };
    }

    const tempPassword = tempPasswordData as string;

    if (!tempPassword || tempPassword.length < 6) {
      return { success: false, error: 'Mot de passe temporaire invalide généré' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/create-user`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': envConfig.supabaseAnonKey,
      },
      body: JSON.stringify({
        username,
        firstName,
        lastName,
        password: tempPassword,
        role,
        siteIds: siteIds || undefined,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la création de l\'utilisateur' };
    }

    // Return success with the temporary password
    return { success: true, tempPassword };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Update an existing user (Admin only) - Uses Edge Function
export async function updateUser(
  userId: string,
  username: string,
  firstName: string,
  lastName: string,
  role: UserRole,
  siteIds?: string[]
): Promise<AuthResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/update-user`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': envConfig.supabaseAnonKey,
      },
      body: JSON.stringify({
        userId,
        username,
        firstName,
        lastName,
        role,
        siteIds: siteIds || undefined,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la mise à jour de l\'utilisateur' };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Toggle user active status (Admin only) - Uses Edge Function
export async function toggleUserStatus(userId: string, isActive: boolean): Promise<AuthResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/toggle-user-status`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': envConfig.supabaseAnonKey,
      },
      body: JSON.stringify({
        userId,
        isActive,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la modification du statut' };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Reset user password (Admin only)
export async function resetUserPassword(userId: string): Promise<AuthResponse & { tempPassword?: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Non authentifié' };
    }

    // Generate temporary password using SQL function
    const { data: tempPasswordData, error: generateError } = await supabase
      .rpc('generate_temp_password');

    if (generateError) {
      return { success: false, error: 'Erreur lors de la génération du mot de passe temporaire' };
    }

    if (!tempPasswordData) {
      return { success: false, error: 'Erreur lors de la génération du mot de passe temporaire' };
    }

    const tempPassword = tempPasswordData as string;

    if (!tempPassword || tempPassword.length < 6) {
      return { success: false, error: 'Mot de passe temporaire invalide généré' };
    }

    const apiUrl = `${envConfig.supabaseUrl}/functions/v1/create-user`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'apikey': envConfig.supabaseAnonKey,
      },
      body: JSON.stringify({
        userId: userId,
        newPassword: tempPassword,
        resetPassword: true,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Erreur lors de la réinitialisation du mot de passe' };
    }

    // Set password reset metadata via SECURITY DEFINER RPC (column-level privileges
    // prevent direct writes to password_reset_at/password_reset_by)
    const { error: rpcError } = await supabase.rpc('set_password_reset_metadata', {
      p_user_id: userId,
    });

    if (rpcError) {
      return { success: false, error: 'Erreur lors de la mise à jour du statut' };
    }

    return { success: true, tempPassword };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}

export async function getActiveUsers(): Promise<{ success: boolean; error?: string; data?: User[] }> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('is_active', true)
      .order('last_name');

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, error: 'Une erreur est survenue' };
  }
}
